import * as React from "react";
import PropTypes from "prop-types";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Container, alpha, Modal, Rating, Button, TextField } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import CustomCard from "./customCard";
import {
  getStudentsRequests,
  getSudentApprovedRequests,
  pendingStudentRequests,
  pendingStudentRequestsNotApproved,
  getParentRequests,
  getBusinessRequests
} from "../../state/slices/request";
import { Snackbar, Alert } from "@mui/material";
import { clearStudent, makeReview } from "../../state/slices/studnent";
import { Star } from "@mui/icons-material";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

export default function StudentRequest() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [openModal, setOpenModal] = React.useState(false);
  const handlModaleOpen = () => setOpenModal(true);
  const handlModalClose = () => setOpenModal(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const review = useSelector((state) => state.student);
  const [value, setValue] = React.useState(0);
  const dispatch = useDispatch();
  const { loading, studentRequests } = useSelector((state) => state.request);
  const { success, error } = useSelector((state) => state.student);
  const user = useSelector((state) => state.user.user);
  const isParent = user?.roles?.includes("PARENT");
  const isBusiness = user?.roles?.includes("BUSINESS");

  React.useEffect(() => {
    if (isParent) {
      dispatch(getParentRequests());
    } else if (isBusiness) {
      dispatch(getBusinessRequests());
    } else {
      dispatch(pendingStudentRequestsNotApproved());
    }
  }, [dispatch, isParent, isBusiness]);

  React.useEffect(() => {
    if (success || error) {
      setSnackbarOpen(true);
    }
  }, [success, error]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) {
      if (isParent) {
        dispatch(getParentRequests());
      } else if (isBusiness) {
        dispatch(getBusinessRequests());
      } else {
        dispatch(pendingStudentRequestsNotApproved());
      }
    } else if (newValue === 1) {
      if (isParent) {
        dispatch(getParentRequests());
      } else if (isBusiness) {
        dispatch(getBusinessRequests());
      } else {
        dispatch(getSudentApprovedRequests());
      }
    }
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const ReviewModal = ({ tutorId }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [rating, setRating] = React.useState(0);
    const [comment, setComment] = React.useState("");
    const [errors, setErrors] = React.useState({});

    const handleFormSubmit = (event) => {
      event.preventDefault();
      const isValid = validate();
      if (isValid) {
        setSnackbarOpen(true);
        dispatch(makeReview({
          tutorId,
          rating,
          comment,
        }));
        handlModalClose();
      }
    };

    const validate = () => {
      let errors = {};
      if (rating === 0) errors.rating = t('studentRequest.ratingRequired');
      if (comment === "") errors.comment = t('studentRequest.commentRequired');
      setErrors(errors);
      return Object.keys(errors).length === 0;
    };

    return (
      <Box>
        <Modal 
          open={openModal} 
          onClose={handlModalClose}
          BackdropProps={{
            sx: {
              bgcolor: alpha('#000', 0.5),
              backdropFilter: 'blur(4px)',
            }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
              p: 4,
              maxHeight: '90vh',
              overflowY: 'auto',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <form onSubmit={handleFormSubmit}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    mb: 2,
                  }}
                >
                  <Star sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  {t('studentRequest.addReview')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('studentRequest.reviewSubtitle')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                    {t('studentRequest.rating')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Rating
                      name="simple-controlled"
                      value={rating}
                      onChange={(event, newValue) => {
                        setRating(newValue);
                      }}
                      size="large"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: theme.palette.primary.main,
                        }
                      }}
                    />
                  </Box>
                  {errors.rating && (
                    <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                      {errors.rating}
                    </Typography>
                  )}
                </Box>
                <TextField
                  label={t('studentRequest.comment')}
                  multiline
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  variant="outlined"
                  fullWidth
                  error={!!errors.comment}
                  helperText={errors.comment}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      }
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handlModalClose}
                    fullWidth
                    sx={{ 
                      borderRadius: 2,
                      py: 1.25,
                      fontWeight: 600,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.25,
                      fontWeight: 600,
                      background: theme.palette.primary.main,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: theme.palette.primary.dark,
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    {t('studentRequest.submit')}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </Modal>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbarOpen(false);
          dispatch(clearStudent());
          if (success && !success.includes("Review")) {
            dispatch(pendingStudentRequestsNotApproved());
          }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={success ? "success" : "error"} 
          sx={{ borderRadius: 2 }}
        >
          {success || error}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 4,
            textAlign: 'center',
          }}
        >
          {t('studentRequest.myRequests')}
        </Typography>

        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <AppBar 
            position="static" 
            sx={{
              bgcolor: 'white',
              boxShadow: 'none',
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  fontSize: '1rem',
                }
              }}
            >
              <Tab label={t('studentRequest.pending')} {...a11yProps(0)} />
              <Tab label={t('studentRequest.approved')} {...a11yProps(1)} />
            </Tabs>
          </AppBar>
        </Box>

        <SwipeableViews
          axis={theme.direction === "rtl" ? "x-reverse" : "x"}
          index={value}
          onChangeIndex={handleChangeIndex}
        >
          <TabPanel value={value} index={0} dir={theme.direction}>
            <CustomCard
              requests={studentRequests}
              loading={loading}
              value={value}
              ReviewModal={ReviewModal}
              handlModaleOpen={handlModaleOpen}
            />
          </TabPanel>
          <TabPanel value={value} index={1} dir={theme.direction}>
            <CustomCard
              requests={studentRequests}
              loading={loading}
              value={value}
              ReviewModal={ReviewModal}
              handlModaleOpen={handlModaleOpen}
            />
          </TabPanel>
        </SwipeableViews>
      </Container>
    </Box>
  );
}
